import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import cx from 'classnames';

import { CopyIcon } from '@motorway/mw-highway-code';

import { Tooltip } from 'Storybook/core/Tooltip';
import { applyCypressData } from 'Utilities/helpers';

import styles from './VrmWithCopy.module.scss';

interface VrmWithCopyProps {
	background?: 'white' | 'yellow';
	containerClass?: string;
	onCopyClicked?: () => void;
	vrm: string;
}

export const HIDE_TOOLTIP_AFTER_MS = 2000;

export const VrmWithCopy: React.FC<VrmWithCopyProps> = ({
	background = 'yellow',
	containerClass,
	onCopyClicked,
	vrm,
}) => {
	const [tooltipText, setTooltipText] = useState('Copied');
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);

	const onCopySuccessful = () => {
		setIsTooltipOpen(true);
		setTooltipText('Copied');
	};

	const onCopyFailed = () => {
		setIsTooltipOpen(true);
		setTooltipText('Failed to copy');
	};

	const onCopy = async () => {
		try {
			onCopyClicked?.();

			await navigator.clipboard.writeText(vrm);

			onCopySuccessful();
		} catch {
			onCopyFailed();
		} finally {
			setTimeout(() => {
				setIsTooltipOpen(false);
			}, HIDE_TOOLTIP_AFTER_MS);
		}
	};

	const onKeyUp = async (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter') {
			await onCopy();
		}
	};

	return (
		<Tooltip content={tooltipText} open={isTooltipOpen}>
			<div
				className={cx(styles[background], styles.vrm, containerClass)}
				{...applyCypressData('vrmWithCopy')}
				onClick={onCopy}
				onKeyUp={onKeyUp}
				tabIndex={0}
			>
				<span className={styles.text}>{vrm}</span>
				<span className={styles.icon}>
					<CopyIcon size="xsmall" />
				</span>
			</div>
		</Tooltip>
	);
};
